import { useCallback, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import { BudgetVersionType, DATE_FORMATS } from '@callmart/types';
import ResponsiveDialog from '../UI/Dialogs/ResponsiveDialog';
import {
    useCreateBudgetVersion,
    useCreateSnapshot,
    useUpdateBudgetVersion,
    useDeleteBudgetVersion,
} from '../../hooks/api/budgetVersions';
import { copyToClipboard } from '../../utils';
import { useDispatchSuccessNotification } from '../../hooks';
import BucketSelect from './BucketSelect';
import dayjs from '@utils/dayjs';

interface BudgetVersionSelectorProps {
    selectedVersionId: number | undefined;
    // CALL-800: the second argument names the version's bucket for a version the list does not hold
    // yet (a draft is selected the moment it is created, before the versions query refetches), so the
    // bucket filter can follow a draft created in another bucket.
    onVersionChange: (versionId: number | undefined, bucketIdHint?: number) => void;
    onDownload: () => void;
    isDownloading: boolean;
    versions: Array<{
        id: number;
        name: string;
        type: string;
        createdAt: Date | string;
        // CALL-758: every version belongs to a bucket; `bucket` is the joined record (name shown
        // next to the version name).
        budgetBucketId: number;
        bucket?: { id: number; name: string } | null;
    }>;
    // CALL-758: buckets to choose from (create dialog + overview bucket filter).
    buckets: Array<{ id: number; name: string }>;
    // CALL-758: overview bucket filter — undefined = "All buckets" (show every version).
    selectedBucketId: number | undefined;
    setSelectedBucketId: (id: number | undefined) => void;
    isLoadingVersions: boolean;
    canEdit: boolean;
    // CALL-765: the consolidated scenarios page only needs the baseline picker. Download/Publish/
    // draft/rename/delete all act on the plain baseline version — offering them next to scenario-
    // adjusted numbers would export/freeze different figures than the ones on screen.
    pickerOnly?: boolean;
}

const BudgetVersionSelector = ({
    selectedVersionId,
    onVersionChange,
    onDownload,
    isDownloading,
    versions,
    buckets,
    selectedBucketId,
    setSelectedBucketId,
    isLoadingVersions,
    canEdit,
    pickerOnly = false,
}: BudgetVersionSelectorProps) => {
    const { t } = useTranslation('budget');
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const nextVersionName = `Budget ${versions.length + 1}`;
    const [newVersionName, setNewVersionName] = useState('');
    const [cloneFromId, setCloneFromId] = useState<number | ''>('');
    const [bucketIdForCreate, setBucketIdForCreate] = useState<number | ''>('');

    const { mutate: createVersion, isLoading: isCreating } = useCreateBudgetVersion({
        onSettled: (data, error) => {
            if (!error && data) {
                setShowCreateDialog(false);
                setNewVersionName('');
                setCloneFromId('');
                setBucketIdForCreate('');
                // CALL-800: the dialog can put the draft in a bucket other than the one being
                // filtered, and the new draft is not in `versions` yet — so its bucket is handed over
                // with it, and the filter moves to where the draft actually is.
                onVersionChange(data.id, data.budgetBucketId);
            }
        },
    });

    // CALL-758: resolve a version's bucket name from the buckets list FIRST (it refetches on a
    // rename) so chips update immediately; fall back to the joined record if the bucket isn't in
    // the list yet.
    const bucketNameOf = useCallback(
        (v: { budgetBucketId: number; bucket?: { name: string } | null }) =>
            buckets.find((b) => b.id === v.budgetBucketId)?.name ?? v.bucket?.name ?? '',
        [buckets]
    );

    const { mutate: snapshot, isLoading: isSnapshotting } = useCreateSnapshot();

    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const { mutate: updateVersion, isLoading: isRenaming } = useUpdateBudgetVersion({
        onSettled: (_, error) => {
            if (!error) setRenameDialogOpen(false);
        },
    });

    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const { mutate: deleteVersion } = useDeleteBudgetVersion({
        onSettled: (_, error, deletedId) => {
            if (!error) {
                setDeleteConfirmId(null);
                if (deletedId === selectedVersionId) {
                    onVersionChange(undefined);
                }
            }
        },
    });

    const selectedVersion = versions.find((v) => v.id === selectedVersionId);
    const isSnapshot = selectedVersion?.type === BudgetVersionType.SNAPSHOT;
    const versionPendingDelete = versions.find((v) => v.id === deleteConfirmId);

    // CALL-758: with a bucket selected the dropdown lists only that bucket's versions (and the
    // per-version bucket badge is redundant, so it's hidden). "All buckets" shows everything.
    //
    // CALL-800: the list needs no exception for a selected version outside the filter, and the filter
    // needs no version reconciliation of its own. BudgetContext resolves the version INSIDE the
    // selected bucket, so the pair can no longer disagree — however the bucket was changed, here or on
    // a page that has no version selector at all.
    const inBucketVersions =
        selectedBucketId == null ? versions : versions.filter((v) => v.budgetBucketId === selectedBucketId);

    // CALL-758: a draft belongs to one bucket, and cloning stays WITHIN that bucket (silos). The
    // bucket picked in the dialog is the target; cloning is limited to that bucket's versions, so a
    // draft can never be seeded across silos. Block create if the target bucket already has a draft.
    const targetBucketId = bucketIdForCreate === '' ? undefined : (bucketIdForCreate as number);
    const cloneableVersions = targetBucketId == null ? [] : versions.filter((v) => v.budgetBucketId === targetBucketId);
    const targetBucketHasDraft =
        targetBucketId != null &&
        versions.some((v) => v.type === BudgetVersionType.DRAFT && v.budgetBucketId === targetBucketId);
    const isCreateDisabled = !newVersionName.trim() || targetBucketId == null || targetBucketHasDraft;

    // Changing the bucket clears a stale clone source (it may belong to the previously picked bucket).
    const handleCreateBucketChange = useCallback((value: number | '') => {
        setBucketIdForCreate(value);
        setCloneFromId('');
    }, []);

    const openRenameDialog = useCallback(() => {
        if (selectedVersion) {
            setRenameValue(selectedVersion.name);
            setRenameDialogOpen(true);
        }
    }, [selectedVersion]);

    const handleRename = useCallback(() => {
        if (selectedVersionId && renameValue.trim()) {
            updateVersion({ id: selectedVersionId, name: renameValue.trim() });
        }
    }, [selectedVersionId, renameValue, updateVersion]);

    const handleVersionChange = useCallback(
        (event: SelectChangeEvent<number | ''>) => {
            const value = event.target.value;
            onVersionChange(value === '' ? undefined : (value as number));
        },
        [onVersionChange]
    );

    // CALL-770: copy the selected version's numeric id so it can be pasted into the export-data API.
    const handleCopyVersionId = useCallback(async () => {
        if (selectedVersionId == null) return;
        await copyToClipboard(String(selectedVersionId));
        dispatchSuccessNotification({ message: t('version.idCopied', { id: selectedVersionId }) });
    }, [selectedVersionId, dispatchSuccessNotification, t]);

    const handleCreateVersion = useCallback(() => {
        if (!newVersionName.trim() || targetBucketId == null) return;
        const isCloning = cloneFromId !== '';
        createVersion({
            name: newVersionName.trim(),
            // CALL-758: always send the chosen bucket; when cloning, the source is restricted to that
            // same bucket, so the backend's same-bucket clone check always passes.
            budgetBucketId: targetBucketId,
            ...(isCloning ? { cloneFromId: cloneFromId as number } : {}),
        });
    }, [newVersionName, targetBucketId, cloneFromId, createVersion]);

    return (
        // No bottom margin: BudgetLayout renders this as its own GridContainer item, so the row spacing
        // below it belongs to that container. An `mb` here added a second 16px on top of the grid gap,
        // which read as a gulf between this header and the Overview's filter bar (CALL-815 QA).
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            {/* CALL-758: bucket filter — "All buckets" lists every version; a specific bucket
                narrows the version dropdown to it and hides the per-version bucket badge. */}
            <BucketSelect buckets={buckets} value={selectedBucketId} onChange={setSelectedBucketId} minWidth={200} />
            <FormControl size="small" sx={{ minWidth: 340 }}>
                <InputLabel>{t('field.version')}</InputLabel>
                <Select
                    value={selectedVersionId ?? ''}
                    onChange={handleVersionChange}
                    label={t('field.version')}
                    disabled={isLoadingVersions}
                >
                    {inBucketVersions.map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {v.name}
                                {/* CALL-770: surface the numeric version id — it's the value the
                                    export-data API expects, and the copy button copies it. */}
                                <Typography variant="caption" color="text.secondary" className="notranslate">
                                    #{v.id}
                                </Typography>
                                {/* CALL-758: show the bucket next to the version name — only in the
                                    "All buckets" view, where versions span buckets. A filled chip
                                    with a tag icon reads distinctly from the "Freigegeben" chip.
                                    Under a filter every entry is that bucket's, so the chip would
                                    repeat the filter on every line. */}
                                {selectedBucketId == null && bucketNameOf(v) && (
                                    <Chip
                                        icon={<LabelOutlinedIcon />}
                                        label={bucketNameOf(v)}
                                        size="small"
                                        color="default"
                                        variant="filled"
                                    />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {dayjs(v.createdAt).format(DATE_FORMATS.DATE)}
                                </Typography>
                                {v.type === BudgetVersionType.SNAPSHOT && (
                                    <Chip
                                        label={t('version.statusReleased')}
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* CALL-770: copy the numeric version id for the export-data API. Available for any
                selected version (incl. snapshots) and to read-only users. */}
            <IconButton
                size="small"
                onClick={handleCopyVersionId}
                disabled={!selectedVersionId}
                title={t('version.copyId')}
                aria-label={t('version.copyId')}
            >
                <ContentCopyIcon fontSize="small" />
            </IconButton>

            {!pickerOnly && canEdit && selectedVersionId && !isSnapshot && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                        size="small"
                        onClick={openRenameDialog}
                        title={t('version.rename')}
                        aria-label={t('version.rename')}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => setDeleteConfirmId(selectedVersionId)}
                        title={t('version.delete')}
                        aria-label={t('version.delete')}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            {!pickerOnly && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {canEdit && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                // CALL-758: default the bucket to the current version's (so the new
                                // draft lands in the same silo by default); the dialog still lets
                                // you change it.
                                setNewVersionName(nextVersionName);
                                setCloneFromId('');
                                setBucketIdForCreate(
                                    selectedBucketId ?? selectedVersion?.budgetBucketId ?? buckets[0]?.id ?? ''
                                );
                                setShowCreateDialog(true);
                            }}
                        >
                            <span>{t('version.newDraft')}</span>
                        </Button>
                    )}

                    {canEdit && (
                        <LoadingButton
                            variant="outlined"
                            size="small"
                            startIcon={<SaveIcon />}
                            onClick={() => selectedVersionId && snapshot(selectedVersionId)}
                            loading={isSnapshotting}
                            disabled={!selectedVersionId || isSnapshot}
                        >
                            <span>{t('action.publish')}</span>
                        </LoadingButton>
                    )}

                    <LoadingButton
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={onDownload}
                        loading={isDownloading}
                        disabled={!selectedVersionId}
                    >
                        <span>{t('action.download')}</span>
                    </LoadingButton>
                </Box>
            )}

            <ResponsiveDialog
                open={showCreateDialog}
                onClose={() => setShowCreateDialog(false)}
                title={t('version.create')}
                isConfirmationDialog
                onConfirm={handleCreateVersion}
                confirmButtonText={t('action.create')}
                isLoading={isCreating}
                disableConfirmButton={isCreateDisabled}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 350 }}>
                    <TextField
                        label={t('field.versionName')}
                        value={newVersionName}
                        onChange={(e) => setNewVersionName(e.target.value)}
                        size="small"
                        autoFocus
                        required
                    />
                    {/* CALL-758: pick the bucket for the new draft. It's the primary choice — cloning
                        below is limited to this bucket's versions, so a draft can't cross a silo. */}
                    <FormControl size="small" required error={targetBucketHasDraft}>
                        <InputLabel>{t('field.bucket')}</InputLabel>
                        <Select
                            value={bucketIdForCreate}
                            onChange={(e) =>
                                handleCreateBucketChange(e.target.value === '' ? '' : Number(e.target.value))
                            }
                            label={t('field.bucket')}
                        >
                            {buckets.map((b) => (
                                <MenuItem key={b.id} value={b.id}>
                                    {b.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {targetBucketHasDraft && <FormHelperText>{t('version.draftExists')}</FormHelperText>}
                    </FormControl>
                    {/* CALL-758: clone source is scoped to the selected bucket — no cross-bucket clone.
                        Disabled until a bucket is chosen; resets when the bucket changes. */}
                    <FormControl size="small" disabled={targetBucketId == null}>
                        <InputLabel>{t('version.cloneFrom')}</InputLabel>
                        <Select
                            value={cloneFromId}
                            onChange={(e) => {
                                const val = e.target.value === '' ? '' : Number(e.target.value);
                                setCloneFromId(val);
                            }}
                            label={t('version.cloneFrom')}
                        >
                            <MenuItem value="">
                                <em>{t('common.none')}</em>
                            </MenuItem>
                            {cloneableVersions.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                    {v.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </ResponsiveDialog>

            <ResponsiveDialog
                open={renameDialogOpen}
                onClose={() => setRenameDialogOpen(false)}
                title={t('version.rename')}
                isConfirmationDialog
                onConfirm={handleRename}
                confirmButtonText={t('action.save')}
                isLoading={isRenaming}
                disableConfirmButton={!renameValue.trim()}
            >
                <Box sx={{ mt: 1, minWidth: 350 }}>
                    <TextField
                        label={t('field.versionName')}
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        size="small"
                        fullWidth
                        autoFocus
                    />
                </Box>
            </ResponsiveDialog>

            <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} maxWidth="sm">
                <DialogTitle>{t('version.delete')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('dialog.deleteVersion', {
                            name: versionPendingDelete?.name ?? '',
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmId(null)} variant="outlined">
                        {t('action.cancel')}
                    </Button>
                    <Button
                        onClick={() => deleteConfirmId && deleteVersion(deleteConfirmId)}
                        variant="contained"
                        color="error"
                    >
                        {t('action.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BudgetVersionSelector;
